import { apiSlice } from './apiSlice'

export interface BracketMatch {
  team_plaintiff_id: string
  team_defendant_id: string
}

export interface BracketRecord {
  id?: string
  case_id: string
  status: string
  matches: BracketMatch[]
  created_at?: string
  created_by?: string
  confirmed_at?: string | null
  confirmed_by?: string | null
}

export interface RoleAssignmentSheet {
  id?: string
  case_id: string
  title: string
  file_name: string
  storage_path: string
  file_size: number
  mime_type: string
  uploaded_by: string
  uploaded_at?: string
}

export const bracketsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    drawBracket: builder.mutation<BracketRecord, { case_id: string }>({
      query: (body) => ({ url: '/brackets/draw', method: 'POST', body }),
      invalidatesTags: ['Bracket', 'Case'],
    }),
    getLatestBracket: builder.query<BracketRecord | null, string>({
      query: (caseId) => ({
        url: '/brackets/latest',
        params: { case_id: caseId },
      }),
      providesTags: (result, err, caseId) => [{ type: 'Bracket', id: caseId }],
    }),
    confirmBracket: builder.mutation<BracketRecord, string>({
      query: (bracketId) => ({
        url: `/brackets/${bracketId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: ['Bracket', 'Case'],
    }),
    listRoleSheets: builder.query<RoleAssignmentSheet[], string>({
      query: (caseId) => ({
        url: '/role-assignment-sheets/list',
        params: { case_id: caseId },
      }),
      providesTags: (res, err, caseId) => [{ type: 'RoleSheet', id: caseId }],
    }),
    uploadRoleSheet: builder.mutation<
      RoleAssignmentSheet,
      { caseId: string; title?: string; file: File }
    >({
      query: ({ caseId, title, file }) => {
        const fd = new FormData()
        fd.append('case_id', caseId)
        if (title) fd.append('title', title)
        fd.append('file', file)
        return {
          url: '/role-assignment-sheets/upload',
          method: 'POST',
          body: fd,
        }
      },
      invalidatesTags: (res, err, { caseId }) => [{ type: 'RoleSheet', id: caseId }],
    }),
    deleteRoleSheet: builder.mutation<{ message: string }, string>({
      query: (sheetId) => ({
        url: `/role-assignment-sheets/${sheetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RoleSheet'],
    }),
    downloadRoleSheet: builder.query<Blob, string>({
      query: (sheetId) => ({
        url: `/role-assignment-sheets/${sheetId}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
})

export const {
  useDrawBracketMutation,
  useGetLatestBracketQuery,
  useConfirmBracketMutation,
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
} = bracketsApi
